<template>
  <div class="stats-container">
    <div class="header">
      <h1 class="title">{{ pageTitle }}</h1>
    </div>

    <div class="stats-content">
      <div class="team-section">
        <div class="team-title">主队</div>
        <div class="team-stats-grid">
          <div class="stat-card">
            <div class="stat-label">射偏</div>
            <div class="stat-controls">
              <button class="btn btn-minus" @click="decreaseShotsOffTarget('home')" :disabled="homeShotsOffTarget <= 0">
                <span class="btn-icon">−</span>
              </button>
              <div class="stat-value">{{ homeShotsOffTarget }}</div>
              <button class="btn btn-plus" @click="increaseShotsOffTarget('home')">
                <span class="btn-icon">+</span>
              </button>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-label">射正</div>
            <div class="stat-controls">
              <button class="btn btn-minus" @click="decreaseShotsOnTarget('home')" :disabled="homeShotsOnTarget <= 0">
                <span class="btn-icon">−</span>
              </button>
              <div class="stat-value">{{ homeShotsOnTarget }}</div>
              <button class="btn btn-plus" @click="increaseShotsOnTarget('home')">
                <span class="btn-icon">+</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="team-section">
        <div class="team-title">客队</div>
        <div class="team-stats-grid">
          <div class="stat-card">
            <div class="stat-label">射偏</div>
            <div class="stat-controls">
              <button class="btn btn-minus" @click="decreaseShotsOffTarget('away')" :disabled="awayShotsOffTarget <= 0">
                <span class="btn-icon">−</span>
              </button>
              <div class="stat-value">{{ awayShotsOffTarget }}</div>
              <button class="btn btn-plus" @click="increaseShotsOffTarget('away')">
                <span class="btn-icon">+</span>
              </button>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-label">射正</div>
            <div class="stat-controls">
              <button class="btn btn-minus" @click="decreaseShotsOnTarget('away')" :disabled="awayShotsOnTarget <= 0">
                <span class="btn-icon">−</span>
              </button>
              <div class="stat-value">{{ awayShotsOnTarget }}</div>
              <button class="btn btn-plus" @click="increaseShotsOnTarget('away')">
                <span class="btn-icon">+</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="reset-section">
        <button class="btn-reset" @click="resetStats">重置</button>
      </div>

      <transition name="fade">
        <div v-if="saveTip" class="save-tip">已保存</div>
      </transition>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'

export default {
  name: 'ShezhenAndshemen',
  setup() {
    const route = useRoute()
    const homeShotsOffTarget = ref(0)
    const homeShotsOnTarget = ref(0)
    const awayShotsOffTarget = ref(0)
    const awayShotsOnTarget = ref(0)
    const saveTip = ref(false)
    const loading = ref(false)
    let saveTipTimer = null

    const homeTeamId = ref(null)
    const awayTeamId = ref(null)
    const matchId = ref(null)

    const pageTitle = computed(() => '主客队射正射偏统计')

    const getStorageKey = (key) => {
      return `match_${matchId.value || 'default'}_${key}`
    }

    const fetchStatistics = async () => {
      const currentMatchId = route.query.match_id || route.params.match_id
      if (!currentMatchId) {
        console.warn('未找到 match_id，跳过获取统计数据')
        return
      }

      matchId.value = currentMatchId
      loading.value = true

      try {
        const response = await fetch(`https://api.aiyqzx.com/app/v1/other/match_statistics?match_id=${currentMatchId}`)
        const result = await response.json()

        if (result.code === 1 && result.data) {
          const homeData = result.data.team_A || {}
          const awayData = result.data.team_B || {}
          homeTeamId.value = homeData.team_id || null
          awayTeamId.value = awayData.team_id || null
          homeShotsOffTarget.value = homeData.shots_off_target || 0
          homeShotsOnTarget.value = homeData.shots_on_target || 0
          awayShotsOffTarget.value = awayData.shots_off_target || 0
          awayShotsOnTarget.value = awayData.shots_on_target || 0
        }
      } catch (error) {
        console.error('获取统计数据失败:', error)
      } finally {
        loading.value = false
      }
    }

    const uploadStatistics = async (teamType) => {
      const isHome = teamType === 'home'
      const teamId = isHome ? homeTeamId.value : awayTeamId.value
      if (!matchId.value || !teamId) {
        console.warn('缺少 match_id 或 team_id，无法上传')
        return
      }

      try {
        const response = await fetch('https://api.aiyqzx.com/app/v1/other/statistics', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            match_id: matchId.value,
            team_id: teamId,
            shots_off_target: isHome ? homeShotsOffTarget.value : awayShotsOffTarget.value,
            shots_on_target: isHome ? homeShotsOnTarget.value : awayShotsOnTarget.value
          })
        })

        const result = await response.json()
        if (result.code !== 1) {
          console.error('上传统计数据失败:', result.msg)
        }
      } catch (error) {
        console.error('上传统计数据失败:', error)
      }
    }

    const saveStats = async (teamType) => {
      try {
        if (teamType === 'home') {
          localStorage.setItem(getStorageKey('homeShotsOffTarget'), homeShotsOffTarget.value.toString())
          localStorage.setItem(getStorageKey('homeShotsOnTarget'), homeShotsOnTarget.value.toString())
        } else {
          localStorage.setItem(getStorageKey('awayShotsOffTarget'), awayShotsOffTarget.value.toString())
          localStorage.setItem(getStorageKey('awayShotsOnTarget'), awayShotsOnTarget.value.toString())
        }

        await uploadStatistics(teamType)

        saveTip.value = true
        if (saveTipTimer) {
          clearTimeout(saveTipTimer)
        }
        saveTipTimer = setTimeout(() => {
          saveTip.value = false
        }, 1500)
      } catch (error) {
        console.error('保存数据失败:', error)
        alert('保存数据失败，请检查浏览器设置')
      }
    }

    const increaseShotsOffTarget = (teamType) => {
      if (teamType === 'home') {
        homeShotsOffTarget.value++
      } else {
        awayShotsOffTarget.value++
      }
      saveStats(teamType)
    }

    const decreaseShotsOffTarget = (teamType) => {
      if (teamType === 'home' && homeShotsOffTarget.value > 0) {
        homeShotsOffTarget.value--
        saveStats(teamType)
      }
      if (teamType === 'away' && awayShotsOffTarget.value > 0) {
        awayShotsOffTarget.value--
        saveStats(teamType)
      }
    }

    const increaseShotsOnTarget = (teamType) => {
      if (teamType === 'home') {
        homeShotsOnTarget.value++
      } else {
        awayShotsOnTarget.value++
      }
      saveStats(teamType)
    }

    const decreaseShotsOnTarget = (teamType) => {
      if (teamType === 'home' && homeShotsOnTarget.value > 0) {
        homeShotsOnTarget.value--
        saveStats(teamType)
      }
      if (teamType === 'away' && awayShotsOnTarget.value > 0) {
        awayShotsOnTarget.value--
        saveStats(teamType)
      }
    }

    const resetStats = () => {
      if (confirm('确定要重置主客队的射正射偏数据吗？')) {
        homeShotsOffTarget.value = 0
        homeShotsOnTarget.value = 0
        awayShotsOffTarget.value = 0
        awayShotsOnTarget.value = 0
        saveStats('home')
        saveStats('away')
      }
    }

    onMounted(async () => {
      await fetchStatistics()
    })

    return {
      pageTitle,
      homeShotsOffTarget,
      homeShotsOnTarget,
      awayShotsOffTarget,
      awayShotsOnTarget,
      saveTip,
      loading,
      increaseShotsOffTarget,
      decreaseShotsOffTarget,
      increaseShotsOnTarget,
      decreaseShotsOnTarget,
      resetStats
    }
  }
}
</script>

<style scoped>
.stats-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  padding: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;
}

.header {
  width: 100%;
  max-width: 900px;
  margin-bottom: 18px;
}

.title {
  font-size: 24px;
  font-weight: bold;
  color: white;
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  margin: 0;
}

.stats-content {
  width: 100%;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.team-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.team-title {
  font-size: 20px;
  font-weight: 700;
  color: white;
  text-align: center;
}

.team-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 16px 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.stat-label {
  font-size: 22px;
  font-weight: 600;
  color: #333;
  text-align: center;
}

.stat-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  width: 100%;
}

.btn {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: none;
  font-size: 30px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.btn:active {
  transform: scale(0.95);
}

.btn-minus {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  color: white;
}

.btn-minus:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-minus:not(:disabled):hover {
  background: linear-gradient(135deg, #ee5a6f 0%, #ff6b6b 100%);
  box-shadow: 0 6px 20px rgba(238, 90, 111, 0.4);
}

.btn-plus {
  background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
  color: white;
}

.btn-plus:hover {
  background: linear-gradient(135deg, #44a08d 0%, #4ecdc4 100%);
  box-shadow: 0 6px 20px rgba(78, 205, 196, 0.4);
}

.btn-icon {
  line-height: 1;
  font-weight: 300;
}

.stat-value {
  font-size: 42px;
  font-weight: bold;
  color: #333;
  min-width: 72px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.reset-section {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 2px;
}

.btn-reset {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid white;
  border-radius: 25px;
  padding: 9px 28px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.btn-reset:active {
  transform: scale(0.95);
}

.btn-reset:hover {
  background: rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.save-tip {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 18px;
  font-weight: 600;
  z-index: 1000;
  pointer-events: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .team-stats-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .stats-container {
    padding: 10px;
  }

  .title {
    font-size: 20px;
  }

  .team-title {
    font-size: 18px;
  }

  .stat-card {
    padding: 14px 10px;
    gap: 10px;
  }

  .stat-label {
    font-size: 20px;
  }

  .stat-value {
    font-size: 36px;
  }

  .btn {
    width: 46px;
    height: 46px;
    font-size: 28px;
  }

  .stat-controls {
    gap: 12px;
  }
}
</style>
